import { createApiClient } from './axios-instance';
import { UserData } from '../contexts/AuthContext';

export interface BaseResponse<T> {
  success: boolean;
  message: string;
  data?: T;
}

// Backend user response format (different from frontend UserData)
interface BackendUserData {
  id: string;
  firebase_uid: string;
  email: string | null;
  email_verified: boolean;
  display_name: string | null;
  photo_url: string | null;
  provider: string;
  is_anonymous: boolean;
  created_at: string;
  last_login: string;
}

class AuthService {
  /**
   * Transform backend user data to frontend UserData format
   */
  private transformUserData(backendData: BackendUserData): UserData {
    return {
      uid: backendData.firebase_uid,
      email: backendData.email,
      display_name: backendData.display_name,
      photo_url: backendData.photo_url,
      provider: backendData.provider,
      is_anonymous: backendData.is_anonymous,
      created_at: backendData.created_at,
      last_login: backendData.last_login,
    };
  }

  /**
   * Verify Firebase ID token with backend
   * Returns UserData directly
   */
  async verifyToken(idToken: string): Promise<UserData> {
    try {
      const client = createApiClient();
      const response = await client.post<BaseResponse<BackendUserData>>('/auth/verify', {
        id_token: idToken,
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Token verification failed');
      }

      if (!response.data.data) {
        throw new Error('No user data in response');
      }

      return this.transformUserData(response.data.data);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Token verification failed';
      console.error('AuthService.verifyToken error:', error);
      throw new Error(message);
    }
  }

  /**
   * Get current user profile from backend
   */
  async getCurrentUser(idToken: string): Promise<UserData> {
    try {
      const client = createApiClient(idToken);
      const response = await client.get<BaseResponse<BackendUserData>>('/auth/me');

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to get current user');
      }

      if (!response.data.data) {
        throw new Error('No user data in response');
      }

      return this.transformUserData(response.data.data);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to get current user';
      console.error('AuthService.getCurrentUser error:', error);
      throw new Error(message);
    }
  }

  /**
   * Create axios instance with auth token
   */
  createAuthenticatedClient(idToken: string) {
    return createApiClient(idToken);
  }
}

export const authService = new AuthService();
